import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Clock, Sparkles } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSaveToggle } from "@/hooks/use-save-toggle";
import { MatchCard } from "@/components/match-card";
import { Pill } from "@/components/ui";

function countdown(target) {
  const ms = Math.max(0, new Date(target).getTime() - Date.now());
  return { label: `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`, done: ms === 0 };
}

/** Today's Top Matches: daily drop (06:00 Africa/Lagos rollover) + countdown + tomorrow teaser. */
export function DailyDrop({ requestLabel, onRequest }) {
  const navigate = useNavigate();
  const toggleSave = useSaveToggle();
  const { user } = useAuth();
  const [, tick] = useState(0);
  const { data, refetch } = useQuery({ queryKey: ["daily"], queryFn: () => api("/matches/daily") });
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);
  const cd = data ? countdown(data.next_refresh_at) : null;
  useEffect(() => { if (cd?.done) refetch(); }, [cd?.done, refetch]);
  if (!data || !data.matches.length) return null;

  return (
    <section className="mb-10" data-testid="daily-drop-section" aria-labelledby="daily-title">
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 id="daily-title" className="text-[28px] leading-tight">Today’s Top Matches</h2>
        <Pill label="New today" tone="brand" icon={Sparkles} testId="daily-drop-new-label" />
        <p className="flex w-full items-center gap-1 text-xs text-muted-foreground" data-testid="daily-drop-countdown">
          <Clock className="h-3.5 w-3.5" />Fresh picks in {cd?.label} · refreshes 6:00 AM Lagos
        </p>
      </div>
      <div className="no-scrollbar -mx-4 flex snap-x gap-4 overflow-x-auto px-4 pb-2 md:mx-0 md:px-0">
        {data.matches.map((m, i) => (
          <div key={m.id} className="w-[176px] shrink-0 snap-start md:w-[210px]">
            <MatchCard index={i} profile={m} requestLabel={requestLabel} testPrefix="daily"
              onView={() => navigate(`/member/${m.id}`)} onRequest={() => onRequest(m)} onToggleSave={() => toggleSave(m.id, m.saved)} />
          </div>
        ))}
      </div>
      {data.tomorrow_verified_count > 0 ? (
        <div className="mt-4 flex items-start gap-3 rounded-[20px] border border-[#C19B53]/50 bg-[#C19B53]/10 p-4" data-testid="daily-drop-tomorrow-teaser">
          <CheckCircle2 className="h-[18px] w-[18px] shrink-0 text-[#C19B53]" />
          <div className="flex-1">
            <p className="text-sm font-bold">
              {data.tomorrow_verified_count} verified {data.tomorrow_verified_count === 1 ? "match is" : "matches are"} waiting tomorrow
            </p>
            {user?.verification_status !== "verified" ? (
              <button data-testid="daily-drop-get-verified-link" onClick={() => navigate("/verification")} className="mt-1 text-sm font-bold text-primary underline-offset-2 hover:underline">
                Get Verified to stand out
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
